/**
 * NotesTab Component
 *
 * Shared wrapper component for displaying notes across all drawer types.
 * Provides consistent layout and overflow handling for the Notes component.
 *
 * Design Decision:
 * - Uses div wrapper (not Container) since each drawer already has its own Container
 * - The wrapper provides size-full and overflow-auto for proper height resolution
 * - This ensures the Notes empty state's h-full resolves correctly in all contexts
 *
 * Modal Integration:
 * - Uses drawer's centralized NoteModal for adding new notes
 * - onOpenCommentModal prop opens the parent modal
 * - Modal appears as overlay, not embedded in drawer
 */

import { Notes } from '@/sections/Notes';
import { Access, Asset, Risk, Seed, Technology } from '@/types';

interface NotesTabProps {
  /** The resource (Asset, Risk, Technology, or Seed) to display notes for */
  resource: Risk | Technology | Asset | Seed;
  /** Resource type string for API calls (e.g., "Asset", "Vulnerability") */
  resourceType: string;
  /** Handler to open the drawer's comment modal when called */
  onOpenCommentModal?: () => void;
  /** Optional: Pass access data from parent (skips internal fetch) */
  accesss?: Access[];
  /** Optional: Custom delete handler for resources like Seeds that use different mutations */
  onRemoveNote?: (index: number) => Promise<void>;
}

/**
 * NotesTab displays notes associated with a resource
 *
 * Used by all drawer types: Asset, Vulnerability, Technology, Seed, Preseed
 *
 * @param resource - The resource object containing notes data
 * @param resourceType - String identifier for the resource type
 * @param onOpenCommentModal - Handler to open the drawer's comment modal
 * @param accesss - Optional access data for resolving user emails
 * @param onRemoveNote - Optional custom delete handler (used by Seeds)
 */
export function NotesTab({
  resource,
  resourceType,
  onOpenCommentModal,
  accesss,
  onRemoveNote,
}: NotesTabProps) {
  return (
    <div className="size-full overflow-auto" id="notes">
      <Notes
        resource={resource}
        resourceType={resourceType}
        onOpenCommentModal={onOpenCommentModal}
        accesss={accesss}
        onRemoveNote={onRemoveNote}
      />
    </div>
  );
}
